import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import type { SportType } from '@/types';
import { getSportOption, SPORT_FILTER_ORDER } from '@/utils/sports';

interface SportFilterProps {
  selectedSport: SportType | null;
  onSelectSport: (sport: SportType | null) => void;
  /** Без верхней границы — сразу под шапкой. */
  attached?: boolean;
}

export const SportFilter: React.FC<SportFilterProps> = ({
  selectedSport,
  onSelectSport,
  attached = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const sportOptions = useMemo(
    () =>
      SPORT_FILTER_ORDER
        .map((sportType) => getSportOption(sportType))
        .filter((sport): sport is NonNullable<typeof sport> => Boolean(sport)),
    []
  );

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setContainerWidth(entry.contentRect.width);
    });

    observer.observe(containerRef.current);
    setContainerWidth(containerRef.current.clientWidth);

    return () => observer.disconnect();
  }, []);

  const estimateChipWidth = (label: string) => {
    const charWidth = 8;
    const horizontalPadding = 42;
    return label.length * charWidth + horizontalPadding;
  };

  const { visibleSports, hiddenSports } = useMemo(() => {
    const gap = 4;
    const allChipWidth = estimateChipWidth('Все');
    const moreButtonWidth = 36;

    let baseVisible: typeof sportOptions;
    let baseHidden: typeof sportOptions;

    if (!containerWidth) {
      baseVisible = sportOptions.slice(0, 4);
      baseHidden = sportOptions.slice(4);
    } else {
      const totalSportsWidth = sportOptions.reduce(
        (sum, sport) => sum + estimateChipWidth(sport.label),
        0
      );
      const totalWidthWithGaps = allChipWidth + totalSportsWidth + gap * sportOptions.length;

      if (totalWidthWithGaps <= containerWidth) {
        baseVisible = sportOptions;
        baseHidden = [];
      } else {
        let usedWidth = allChipWidth + gap;
        const nextVisible: typeof sportOptions = [];

        for (const sport of sportOptions) {
          const nextWidth = estimateChipWidth(sport.label);
          if (usedWidth + nextWidth + gap + moreButtonWidth > containerWidth) break;
          nextVisible.push(sport);
          usedWidth += nextWidth + gap;
        }

        baseVisible = nextVisible;
        baseHidden = sportOptions.slice(nextVisible.length);
      }
    }

    if (selectedSport) {
      const selectedHidden = baseHidden.find((sport) => sport.value === selectedSport);

      if (selectedHidden) {
        const adjustedVisible = [selectedHidden, ...baseVisible];
        const movedBack = baseVisible.length > 0 ? adjustedVisible.pop() : undefined;
        const adjustedHidden = baseHidden.filter((sport) => sport.value !== selectedSport);

        if (movedBack) {
          adjustedHidden.unshift(movedBack);
        }

        return {
          visibleSports: adjustedVisible,
          hiddenSports: adjustedHidden,
        };
      }
    }

    return {
      visibleSports: baseVisible,
      hiddenSports: baseHidden,
    };
  }, [containerWidth, selectedSport, sportOptions]);

  const hiddenHasSelected = hiddenSports.some((sport) => sport.value === selectedSport);

  useEffect(() => {
    if (!hiddenSports.length) {
      setIsExpanded(false);
    }
  }, [hiddenSports.length]);

  return (
    <Box
      className="full"
      sx={{ bgcolor: 'background.paper', borderTop: attached ? 0 : 1, borderBottom: 1, borderColor: 'divider' }}
    >
      <Box
        ref={containerRef}
        className="cont"
        sx={{
          display: 'flex',
          gap: 0,
          alignItems: 'stretch',
          overflow: 'hidden',
          px: 0,
          minHeight: 46,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => onSelectSport(null)}
          sx={{
            border: 'none',
            bgcolor: 'transparent',
            cursor: 'pointer',
            ml: 0.75,
            pl: 1.25,
            pr: 1.25,
            minWidth: 'fit-content',
            color: selectedSport === null ? 'text.primary' : 'text.secondary',
            fontSize: '0.875rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            position: 'relative',
            flexShrink: 0,
            '&::after': {
              content: selectedSport === null ? '""' : 'none',
              position: 'absolute',
              left: 8,
              right: 8,
              bottom: 0,
              height: 3,
              borderRadius: 999,
              bgcolor: 'primary.main',
            },
          }}
        >
          Все
        </Box>

        {visibleSports.map((sport) => {
          const isActive = selectedSport === sport.value;
          return (
            <Box
              key={sport.value}
              component="button"
              type="button"
              onClick={() => onSelectSport(sport.value)}
              sx={{
                border: 'none',
                bgcolor: 'transparent',
                cursor: 'pointer',
                px: 1.25,
                minWidth: 'fit-content',
                color: isActive ? 'text.primary' : 'text.secondary',
                fontSize: '0.875rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                position: 'relative',
                flexShrink: 0,
                '&::after': {
                  content: isActive ? '""' : 'none',
                  position: 'absolute',
                  left: 8,
                  right: 8,
                  bottom: 0,
                  height: 3,
                  borderRadius: 999,
                  bgcolor: 'primary.main',
                },
              }}
            >
              {sport.label}
            </Box>
          );
        })}

        {hiddenSports.length > 0 && (
          <IconButton
            onClick={() => setIsExpanded((prev) => !prev)}
            size="small"
            sx={{
              ml: 'auto',
              mr: 0.25,
              color: hiddenHasSelected ? 'primary.main' : 'text.secondary',
              width: 32,
              height: 32,
              alignSelf: 'center',
            }}
          >
            {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
          </IconButton>
        )}
      </Box>

      {isExpanded && hiddenSports.length > 0 && (
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
        <Box className="cont">
          {hiddenSports.map((sport) => {
            const isActive = selectedSport === sport.value;
            return (
              <Box
                key={sport.value}
                component="button"
                type="button"
                onClick={() => {
                  onSelectSport(sport.value);
                  setIsExpanded(false);
                }}
                sx={{
                  width: '100%',
                  border: 'none',
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: isActive ? 'action.selected' : 'background.paper',
                  color: 'text.primary',
                  cursor: 'pointer',
                  px: 2,
                  py: 1.5,
                  textAlign: 'left',
                  fontSize: '0.9875rem',
                  fontWeight: 700,
                  letterSpacing: '0.01em',
                  textTransform: 'uppercase',
                }}
              >
                {sport.label}
              </Box>
            );
          })}
          <Box sx={{ height: 1 }} />
        </Box>
        </Box>
      )}
    </Box>
  );
};
